export interface SurveyResponseInput {
  rating: number;
  feedback?: string;
  surveyMonth?: Date;
}

export interface SurveyResponseView {
  id: number;
  rating: number;
  feedback?: string;
  submittedAt: Date;
  surveyMonth: Date;
  companyId?: number;
  userId?: string;
  branchId?: number;
  isArchived: boolean;
}

export interface SurveyStatsView {
  month: Date;
  totalResponses: number;
  // CO-815 review 3 finding 1: null below the eNPS anonymity threshold (5 respondents), with insufficientData.
  promoters: number | null;
  passives: number | null;
  detractors: number | null;
  enpsScore: number | null;
  averageRating: number | null;
  insufficientData?: boolean;
}

export interface RecognitionInput {
  recipientId: string;
  message: string;
  values?: string[];
  visibilityType?: FeedbackVisibilityType;
}

export interface RecognitionView {
  id: number;
  senderId: string;
  senderName: string;
  senderProfilePictureUrl?: string;
  recipientId: string;
  recipientName: string;
  recipientProfilePictureUrl?: string;
  message: string;
  values: string[];
  timestamp: string;
  companyId?: number;
  visibilityType: FeedbackVisibilityType;
  isPublic: boolean;
}

export interface CompanyRecognitionValueView {
  id: number;
  name: string;
  description?: string;
  companyId: number;
  createdAt: Date;
  isActive: boolean;
}

export interface CompanyRecognitionValueInput {
  id?: number;
  name: string;
  description?: string;
}

export interface RecognitionRecipientView {
  recipientId: string;
  recipientFirstName: string;
  recipientLastName: string;
  recipientProfilePictureUrl?: string;
}

// Reflection Input Models
export interface MonthlyReflectionInput {
  periodId: number;
  responseText: string;
  responseStatus: string; // "draft" or "submitted"
}

export interface ManagerReflectionResponseInput {
  userReflectionId: number;
  message: string;
  encouragementType?: string;
}

// Reflection View Models
export interface UserReflectionView {
  id: number;
  userId: string;
  userName?: string;
  periodId: number;
  promptId: number;
  responseText: string;
  responseStatus: string;
  submittedAt?: Date;
  createdOn: Date;
  updatedOn: Date;
  companyId?: number;
  branchId?: number;
  departmentId?: number;
  isArchived: boolean;

  // The prompt as it was ASKED, resolved status-blind on the server, in all three languages.
  // Pick with pickReflectionTitle() / pickReflectionCategory() (Reflections/reflectionPromptText.ts).
  promptTitle?: string | null;
  promptTitleAr?: string | null;
  promptTitleFr?: string | null;
  promptCategory?: string | null;
  promptCategoryAr?: string | null;
  promptCategoryFr?: string | null;
  managerResponse?: ManagerReflectionResponseView;
}

export interface ManagerReflectionResponseView {
  id: number;
  message: string;
  encouragementType?: string;
  isRead: boolean;
  readAt?: Date;
  userReflectionId: number;
  managerId: string;
  managerName?: string;
  managerImageUrl?: string;
  createdOn: Date;
  updatedOn: Date;

  // Navigation collections
  actionItems: ReflectionActionItemView[];
  resources: ReflectionResourceView[];
}

export interface ReflectionActionItemView {
  id: number;
  itemText: string;
  displayOrder?: number;
  isCompleted: boolean;
  completedAt?: Date;
}

export interface ReflectionResourceView {
  id: number;
  title: string;
  resourceUrl: string;
  description?: string;
  displayOrder?: number;
}

export interface ReflectionContributionStatusView {
  submissionDeadline: Date;
  isDeadlinePassed: boolean;
  draftCount: number;
  submittedCount: number;
  totalPrompts: number;
  hasContributed: boolean;
}

export interface ReflectionPeriodView {
  id: number;
  periodMonth: number;
  periodYear: number;
  startDate: Date;
  endDate: Date;
  submissionDeadline: Date;
  isActive: boolean;
  isArchived: boolean;
}

export interface ReflectionPrompt {
  id: number;
  /**
   * CO-591 — the STABLE key of a prompt (`ReflectionPrompt.PromptId` on the server, e.g.
   * `proudest-contribution`). `id` is an identity value; an archive-and-replace keeps the key and
   * mints a new id, so never treat `id` as the prompt's name.
   */
  promptId: string;
  // English (base) + Arabic + French per field. Pick with resolvePromptText(); never read the
  // base field directly in a component, or the language switch silently stops working.
  title?: string | null;
  titleAr?: string | null;
  titleFr?: string | null;
  category?: string | null;
  categoryAr?: string | null;
  categoryFr?: string | null;
  question?: string | null;
  questionAr?: string | null;
  questionFr?: string | null;
  subtext?: string | null;
  subtextAr?: string | null;
  subtextFr?: string | null;
  placeholder?: string | null;
  placeholderAr?: string | null;
  placeholderFr?: string | null;
  /** Always arrays (server guarantees), empty when none. */
  tips: string[];
  tipsAr: string[];
  tipsFr: string[];
  iconName?: string | null;
  displayOrder: number;
  isActive: boolean;
  /** Only true on the Operator manage list. */
  isArchived: boolean;
  /** null (absent on the wire) = platform default shared by every company; otherwise the owning company. */
  companyId?: number | null;
  /** Reflections pointing at this exact row (this company's in a company scope, every company's for a platform default); the Operator page warns before archive-and-replace. */
  answerCount: number;
}

/** One row of a reorder: a company-owned prompt and the number it should take. */
export interface ReflectionPromptOrderItem {
  id: number;
  displayOrder: number;
}

/** The Operator's write shape. Mirrors ReflectionPromptInput on the server. */
export interface ReflectionPromptInput {
  /** Set ONLY when customizing a platform default for a company: its stable key, so the new company row shadows it. */
  promptId?: string | null;
  title: string;
  titleAr?: string | null;
  titleFr?: string | null;
  category: string;
  categoryAr?: string | null;
  categoryFr?: string | null;
  question: string;
  questionAr?: string | null;
  questionFr?: string | null;
  subtext?: string | null;
  subtextAr?: string | null;
  subtextFr?: string | null;
  placeholder?: string | null;
  placeholderAr?: string | null;
  placeholderFr?: string | null;
  tips: string[];
  tipsAr: string[];
  tipsFr: string[];
  iconName: string;
  displayOrder: number;
  isActive: boolean;
}

export interface GoalMilestoneInput {
  title: string;
  dueDate: string;
  completed: boolean;
}

export interface GoalMilestoneView extends GoalMilestoneInput {
  id: number;
  goalId: string;
  completedAt?: Date;
  createdOn: Date;
  updatedOn: Date;
  isArchived: boolean;
}

export interface GoalInput {
  title: string;
  description: string;
  level: 'company' | 'team' | 'individual';
  status: 'on-track' | 'at-risk' | 'completed' | 'overdue';
  progress: number;
  ownerId: string;
  department: string;
  startDate: string;
  dueDate: string;
  parentId?: string;
  milestones?: GoalMilestoneInput[];
}

export interface GoalView {
  id: string;
  title: string;
  description: string;
  level: 'company' | 'team' | 'individual';
  status: 'on-track' | 'at-risk' | 'completed' | 'overdue';
  progress: number;
  ownerId: string;
  ownerName: string;
  department: string;
  startDate: string;
  dueDate: string;
  parentId?: string;
  parentTitle?: string;
  createdOn: Date;
  updatedOn: Date;
  companyId?: number;
  branchId?: number;
  isArchived: boolean;

  // Navigation properties
  milestones: GoalMilestoneView[];
  subGoals: GoalView[];
  linkedMetrics?: GoalMetricView[];
}

export interface GoalMetricView {
  id: number;
  goalId: string;
  metricType: string;
  currentValue: number;
  targetValue: number;
  unit: string;
  createdOn: Date;
  updatedOn: Date;
  isArchived: boolean;
}

export interface GoalAlignmentView {
  goal: GoalView;
  parentGoal?: GoalView;
  childGoals: GoalView[];
  allConnections: GoalView[];
}

export interface GoalProgressUpdateInput {
  goalId: string;
  progress: number;
  status: 'on-track' | 'at-risk' | 'completed' | 'overdue';
  notes?: string;
}

export interface GoalProgressUpdateView {
  id: number;
  goalId: string;
  userId: string;
  userName: string;
  previousProgress: number;
  newProgress: number;
  previousStatus: string;
  newStatus: string;
  notes?: string;
  timestamp: Date;
}

export interface GoalFilterOptions {
  search?: string;
  department?: string;
  user?: string;
  dueDate?: string;
  status?: string[];
  levels?: string[];
  parentId?: string;
  startDate?: string;
  endDate?: string;
}

export interface GoalStatsView {
  totalGoals: number;
  onTrackCount: number;
  atRiskCount: number;
  completedCount: number;
  overdueCount: number;
  averageProgress: number;
  byLevel: {
    company: GoalLevelStats;
    team: GoalLevelStats;
    individual: GoalLevelStats;
  };
  byDepartment: {
    [department: string]: GoalLevelStats;
  };
}

export interface GoalLevelStats {
  total: number;
  onTrack: number;
  atRisk: number;
  completed: number;
  overdue: number;
  averageProgress: number;
}

export interface GoalCommentInput {
  goalId: string;
  comment: string;
}

export interface GoalCommentView {
  id: number;
  goalId: string;
  userId: string;
  userName: string;
  userProfilePictureUrl?: string;
  comment: string;
  timestamp: Date;
  isEdited: boolean;
  editedAt?: Date;
}

export interface GoalReminderView {
  id: number;
  goalId: string;
  userId: string;
  reminderDate: Date;
  reminderType: 'weekly' | 'monthly' | 'quarterly' | 'custom';
  isActive: boolean;
  createdOn: Date;
  updatedOn: Date;
}

export interface GoalAttachmentView {
  id: number;
  goalId: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: Date;
}

export interface TeamHubPostInput {
  content: string;
  postType: string;
  department?: string;
  tags?: string[];
  attachmentUrls?: string[];
}

export interface TeamHubPostView {
  id: number;
  authorId: string;
  authorName: string;
  authorAvatar?: string;  // Changed from authorProfilePictureUrl to match backend
  content: string;
  postType: string;
  department?: string;
  tags: string[];
  attachmentUrls: string[];
  postedAt: Date;
  updatedAt?: Date;
  likeCount: number;
  commentCount: number;
  isLikedByUser: boolean;
  companyId?: number;
  branchId?: number;
  isArchived: boolean;
  comments?: TeamHubPostCommentView[];
}

export interface TeamHubPostCommentInput {
  postId: number;
  content: string;
  parentCommentId?: number;
}

export interface TeamHubPostCommentView {
  id: number;
  postId: number;
  authorId: string;
  authorName: string;
  authorAvatar?: string;  // Changed from authorProfilePictureUrl to match backend
  content: string;
  commentedAt: Date;
  updatedAt?: Date;
  likeCount: number;
  isLikedByUser: boolean;
  parentCommentId?: number;
  isArchived: boolean;
  replies?: TeamHubPostCommentView[];
}

export interface TeamHubFilterOptions {
  department?: string;
  postType?: string;
  search?: string;
  tags?: string[];
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

// Add these interfaces to your existing models
export interface DashboardRecognitionView {
  id: number;
  senderId?: string;
  recipientId?: string;
  senderName: string;
  recipientName: string;
  avatarUrl?: string;
  senderImageUrl?: string;
  recipientImageUrl?: string;
  message: string;
  values: string[];
  points: number;
  date: string; // ISO date string
}

export interface RecognitionStatisticsView {
  receivedCount: number;
  givenCount: number;
  totalPointsReceived: number;
  totalPointsGiven: number;
  valuesBreakdown: Record<string, number>;
  monthlyTrend: Record<string, number>;
}

export interface DashboardSummaryView {
  title: string;
  value: string;
  icon: string;
  color: string;
  trend: TrendView;
  description: string;
}

export interface TrendView {
  type: 'up' | 'down' | 'stable';
  value: string;
}

export interface FiltersDto {
  dateRange?: string;
  values?: string;
  category?: string;
  type?: string;
}

export interface GoalDashboardStatsView {
  // Company Stats
  companyTotalGoals: number;
  companyCompletedGoals: number;
  companyOnTrackGoals: number;
  companyAtRiskGoals: number;
  companyOverdueGoals: number;
  companyAverageProgress: number;
  companyCompletionRate: number;
  companyGoalsByLevel: { [key: string]: number };
  companyGoalsByStatus: { [key: string]: number };

  // User Personal Stats
  userTotalGoals: number;
  userCompletedGoals: number;
  userOnTrackGoals: number;
  userAtRiskGoals: number;
  userOverdueGoals: number;
  userAverageProgress: number;
  userCompletionRate: number;
  userGoalsByLevel: { [key: string]: number };
  userGoalsByStatus: { [key: string]: number };

  // Assigned Goals Stats
  assignedTotalGoals: number;
  assignedCompletedGoals: number;
  assignedOnTrackGoals: number;
  assignedAtRiskGoals: number;
  assignedOverdueGoals: number;
  assignedAverageProgress: number;
  assignedCompletionRate: number;

  // Milestone Stats
  totalMilestones: number;
  completedMilestones: number;

  // Recent Activity
  recentGoalsCreated: number;
  recentGoalsCompleted: number;

  // Engagement Metrics
  engagementScore: number;
  activeGoalStreak: number;

  // Metadata
  analysisDate: string;
  companyId: number;
}

export interface GoalProgressIndicator {
  id: number;
  title: string;
  subtitle: string;
  icon: string;
  value: number;
  period: string;
  description: string;
  trend: string;
  change: string;
  category: string;
}
// Interfaces matching the backend ViewModels
export interface SummaryMetricView {
  title: string;
  value: string;
  change: string;
  changeType: string;
  icon: string;
  description: string;
  trend: string;
  timePeriod?: string;
  badge?: string;
  tooltip?: string;
  // Set by the backend when the metric has no measurable population. `value` then carries a locale
  // key instead of a figure and `change` is absent. NullValueHandling.Ignore drops nulls, so the
  // absent case arrives as undefined, never null.
  notMeasured?: boolean;
  // 2026-09-28: with notMeasured, the metric is survey-derived and under the 5-respondent eNPS floor
  // (tooltip = insights.metric.notMeasured.enpsThreshold).
  insufficientData?: boolean;
}

export interface DepartmentOptionView {
  id: number;
  name: string;
}

export interface RecognitionDataView {
  name: string;
  recognitions: number;
}

export interface EngagementTrendView {
  month: string;
  goalAlignment: number;
  reflectionRate: number;
  // 2026-09-28: absent for a month with fewer than 5 survey responses (eNPS anonymity floor);
  // enpsInsufficientData is then true. A gap, never 0.
  enpsScore?: number | null;
  enpsInsufficientData?: boolean;
}

export interface GoalProgressView {
  name: string;
  value: number;
  total: number;
}

// Mirrors Mowazaf.Server/Data/Preformance/ViewModels/FeedbackTagView.cs. This used to be declared
// as `string[]` on FeedbackThemeView, which the server has never sent — InsightService projects a
// full tag object. A consumer that renders a tag must map to `.name`, or React throws "Objects are
// not valid as a React child".
export interface FeedbackTagView {
  id: number;
  name: string;
  color: string;
}

export interface FeedbackThemeView {
  id: number;
  title: string;
  description: string;
  sentiment: string;
  category: string;
  frequency: string;
  mentions: number;
  lastSeen: string;
  tags: FeedbackTagView[];
}

export interface InsightsDashboardView {
  summaryMetrics: SummaryMetricView[];
  recognitionData: RecognitionDataView[];
  engagementTrends: EngagementTrendView[];
  goalProgress: GoalProgressView[];
  feedbackThemes: FeedbackThemeView[];
  lastUpdated: string;
}

export interface InsightsFilterView {
  dateRange: string;
  department: string;
  startDate?: string;
  endDate?: string;
}

/** CO-932 follow-up (review CO932-3) — why a privacy-floored group or Q12 cell is hidden. CO-939: 'archived' = the
 *  Q12 surfaces refuse a department filter on an archived department (Q12 only; Recognition never sends it). */
export type HiddenReason = 'floor' | 'subtraction' | 'archived';

export interface Q12DimensionDataView {
  id: string;
  /** null when the dimension is not measured (nothing analysed yet) — the API returns null,
      and Q12Heatmap.tsx already null-guards at runtime. */
  score: number | null;
  /** null when there is no previous-period row to compare against. An absent baseline is NOT a
      movement of zero — coercing it produced a "trend" equal to the entire score the first time a
      dimension became measurable. */
  trend: number | null;
  /** True when the group is too small to show safely (headcount < 5), or hidden by CO-932 secondary suppression. */
  hidden: boolean;
  /** CO-932 follow-up: why it is hidden. Absent when shown and on older payloads (treat as 'floor'). */
  hiddenReason?: HiddenReason;
  respondentCount: number;
  /** True when there is no score to show, as opposed to `hidden`, where there is one we withhold. */
  isPlaceholder?: boolean;
}

export interface Q12DepartmentColumnView {
  departmentId: number;
  departmentName: string;
  dimensions: Q12DimensionDataView[];
}

export interface Q12HeatmapView {
  company: Q12DimensionDataView[];
  department: Q12DimensionDataView[];
  departmentColumns: Q12DepartmentColumnView[];
  // These scores are recomputed by a daily job, not live.
  calculatedAt?: string;
  nextRefreshAt?: string;
}

export interface EngagementDistributionView {
  engaged: number;
  notEngaged: number;
  activelyDisengaged: number;
  /**
   * The classified people. 2026-10-14 (owner, second round): when measured this is the MEASURED
   * count the split was computed over (the server's split never sums past it), and the card's
   * caption "Figures cover the N people with activity" reads N from here. No separate field.
   */
  total: number;
  // 2026-09-28: nobody classified (fewer than 5 people with an eNPS answer or check-in in 90 days, or a
  // group under 5). Counts and total are then 0.
  notMeasured?: boolean;
  /** Why, when notMeasured: below the 5-person floor, or no engagement index calculated yet. */
  notMeasuredReason?: 'belowFloor' | 'noIndex';
  /** People with no signal: never bucketed. headcount = total + notMeasuredCount. */
  notMeasuredCount?: number;
  headcount?: number;
}

export interface EngagementRiskView {
  title: string;
  scope: string;
  confidence: 'High' | 'Medium' | 'Low';
  dimensions: string[];
  intervention: string;
}

export interface CorrelationDataPointView {
  /** Server's English quartile label; rendered only when `quartileKey` is absent (older server). */
  category: string;
  /** Stable machine key for the quartile; the client renders `engagementImpact.quartile.<key>`. */
  quartileKey?: 'bottom25' | 'lowerMid' | 'upperMid' | 'top25';
  engagement: number;
  outcome: number;
}

export interface EngagementImpactView {
  goalCompletion: CorrelationDataPointView[];
  absenteeism: CorrelationDataPointView[];
  participation: CorrelationDataPointView[];
  /**
   * CO-915: series that have outcome data but are held back by the privacy floor. Absent from a
   * server that predates it, so read it as optional.
   */
  withheld?: Array<'goalCompletion' | 'absenteeism' | 'participation'>;
}

export interface GoalAchievementProgressView {
  goalGetterProgress: number;
  goalGetterTarget: number;
  goalGetterUnlocked: boolean;
  consistentAchieverProgress: number;
  consistentAchieverTarget: number;
  consistentAchieverUnlocked: boolean;
}

// Q12 Insights Drill-Down & Trends
export interface Q12SubFactor {
  name: string;
  score: number | null;
  weight: number;
  actionUrl?: string;
  /** CO-450 (§3.6.4) — employees this SUB-FACTOR could actually score. Distinct from the
      dimension's `eligibleCount` (headcount): a sub-factor silently skips anyone it has no data
      for, so a high score computed over 3 of 40 people used to be indistinguishable from one
      computed over all 40. Optional/nullable because only Q3 populates it in this wave (Q1/Q2/Q7
      have the same silent-skip problem and are filed as follow-ups), and because the field is
      absent on every response produced by a server older than CO-450. */
  measuredCount?: number | null;
}

export interface Q12FactorBreakdown {
  dimensionId: string;
  dimensionName: string;
  overallScore: number | null;
  subFactors: Q12SubFactor[];
  trendHistory: { period: string; score: number | null }[];
  suggestedActions: { title: string; description: string; actionUrl: string; icon: string }[];
  /** CO-450 (§3.6.4) — the measured population behind `overallScore`, and the eligible headcount
      it is measured out of. 🔴 This is NOT the same number as `Q12DimensionDataView.respondentCount`
      on the heatmap, which holds HEADCOUNT and is what the `< 5` hide rule keys on. Measured
      population is computed live and is not persisted anywhere, which is precisely why it can only
      appear here in the drill-down. Do not cross-reference the two. */
  measuredCount?: number | null;
  eligibleCount?: number | null;
  /** CO-450 (§3.6.2) — `yyyy-MM` of the deploy in which this dimension's scoring METHODOLOGY
      changed, or null/absent for a dimension that was never rescored. Q3's weights moved from
      Coverage 1.0 / Utilization 0.0 to 0.4 / 0.6, so every company's Q3 steps at that period and
      the trend line BREAKS there. Annotated on the trend chart so the step does not read as real
      month-over-month movement. A server-side CONSTANT, identical for every tenant. */
  methodologyChangedAtPeriod?: string | null;
  /** True when the scope is under the 5-person floor or hidden by CO-932 secondary suppression:
      every score, count, trend point and suggestion is withheld. */
  hidden?: boolean;
  /** Why it is hidden. Absent when shown and on older payloads (treat as 'floor'). */
  hiddenReason?: HiddenReason;
}

export interface Q12TrendDataView {
  period: string;
  dimensions: Record<string, number | null>;
}

// Feedback Enums
export enum FeedbackVisibilityType {
  Private = 0,
  Public = 1
}

export enum FeedbackType {
  Coaching = 1,
  Concern = 2
}

// Feedback Input Models
export interface FeedbackInput {
  receivedById: string;
  comment: string;
  feedbackType: FeedbackType;
  // Feedback is always private - no visibility option
}

// Feedback View Models
export interface FeedbackView {
  id: number;
  givenById: string;
  givenByName: string;
  receivedById: string;
  receivedByName: string;
  comment: string;
  createdOn: string;
  visibilityType: FeedbackVisibilityType;
  isPublic: boolean;
  feedbackType: FeedbackType;
}

// Recognition & Feedback Config
export interface RecognitionFeedbackConfigView {
  allowPublicRecognitionFeedback: boolean;
}

export interface RecognitionFeedbackConfigInput {
  allowPublicRecognitionFeedback: boolean;
}

// eNPS Analytics
export interface ENPSAnalyticsFilterView {
  dateRange?: string;
  department?: string;
  location?: string;
}

export interface ENPSOverviewView {
  currentScore: number;
  /** 2026-09-28 (S4): the four "vs last month" fields are absent when last month had < 5 answers (no baseline). */
  previousScore?: number | null;
  change?: number | null;
  trend?: 'up' | 'down' | 'stable' | null;
  participationRate: number;
  previousParticipationRate?: number | null;
  /** CO-815 review round 2: null when a filtered slice is withheld for anonymity (`withheld`). */
  totalResponses: number | null;
  eligibleEmployees: number;
  promoters: number;
  passives: number;
  detractors: number;
  promoterPercentage: number;
  passivePercentage: number;
  detractorPercentage: number;
  scoreCategory: 'Strong' | 'Moderate' | 'Developing';
  lastUpdated: string | null;
  insufficientData: boolean;
  /**
   * CO-815 review round 2 (additive): a FILTERED slice hidden by the anonymity guard. Implies
   * insufficientData and totalResponses = null. Optional: an older backend does not send it.
   */
  withheld?: boolean;
}

export interface ENPSTrendPointView {
  // CO-594 — machine-readable month, "2026-09". Format it for display with the app's language;
  // never render it raw. Optional because a tab on the previous bundle can still be served by an
  // older backend during a rollback.
  monthKey?: string;
  // ⚠️ CO-594 — server-formatted display label, kept only for the rollback window. Prefer `monthKey`.
  month: string;
  enpsScore: number;
  participationRate: number;
  /** CO-815 review round 2: null when this month of a filtered slice is withheld (`withheld`). */
  totalResponses: number | null;
  insufficientData: boolean;
  /** CO-815 review round 2 (additive): this month is withheld for anonymity. Never render its count. */
  withheld?: boolean;
}

export interface ENPSSegmentOptionView {
  id: string;
  name: string;
  /** CO-815 review: null below the anonymity threshold; the server withholds small counts. */
  responseCount: number | null;
  hasSufficientData: boolean;
}

export interface ENPSSegmentOptionsView {
  departments: ENPSSegmentOptionView[];
  locations: ENPSSegmentOptionView[];
}

// eNPS Theme Signals
export interface ENPSThemeSignalView {
  id: number;
  theme: string;
  mentionPercentage: number;
  lastCyclePercentage: number;
  changePercent: number;
  isNew: boolean;
  direction: 'up' | 'down' | 'stable';
  // CO-595 — null means the sentiment was never measured for this theme (the summary was refused,
  // timed out, or came back without one). It is NOT the same as 'neutral', which is a real judgement.
  sentiment: 'positive' | 'constructive' | 'neutral' | null;
  mentionCount: number;
  suppressed: boolean;
  themeCatalogId?: number | null;
}

/**
 * One survey month's signals. Months are kept as separate buckets rather than one merged list:
 * mention percentages are per-month shares, so stacking two months makes both of them wrong.
 */
export interface ENPSThemeSignalMonthView {
  surveyMonth: string;
  isCurrent: boolean;
  themes: ENPSThemeSignalView[];
}

// eNPS Focus Areas
export interface ENPSFocusAreaView {
  id: number;
  title: string;
  rationale?: string;
  reviewWindow?: string;
  status: 'ongoing' | 'reviewed' | 'archived';
  startDate: string;
  reviewedDate?: string;
  outcome?: string;
  linkedTheme?: string;
  themeCatalogId?: number | null;
}

export interface ENPSFocusAreaInput {
  title: string;
  rationale?: string;
  reviewWindow?: string;
  linkedTheme?: string;
  themeCatalogId?: number | null;
  // A theme the catalog does not hold yet; the server mints it and links it in the same save.
  newThemeName?: string;
}

export interface ENPSFocusAreaUpdateInput {
  title?: string;
  rationale?: string;
  reviewWindow?: string;
  status?: string;
  outcome?: string;
  themeCatalogId?: number | null;
  // Null already means "leave alone" on a partial update, so unlinking is stated outright.
  clearThemeCatalogId?: boolean;
}

// eNPS Learnings
export interface ENPSLearningView {
  id: number;
  text: string;
  displayOrder: number;
}

export interface ENPSLearningInput {
  text: string;
  displayOrder: number;
}

export interface ENPSLearningBulkInput {
  learnings: ENPSLearningInput[];
}

export interface ENPSExportInput {
  format: 'csv' | 'pdf';
  dateRange: string;
  department: string;
  location: string;
}

// eNPS Theme Catalog
export interface ENPSThemeCatalogView {
  id: number;
  name: string;
  isActive: boolean;
  displayOrder: number;
}

export interface ENPSThemeCatalogInput {
  name: string;
  displayOrder: number;
}

export interface ENPSThemeCatalogUpdateInput {
  name?: string;
  displayOrder?: number;
}

// eNPS Opinion Coverage
export interface ENPSCoverageRowView {
  themeCatalogId: number;
  theme: string;
  mentionCount: number;
  mentionPercentage: number;
  weight: number;
  linkedFocusAreas: string[];
  matchScore?: number | null;
  weightedContribution: number;
}

export interface ENPSCoverageView {
  coveragePercentage?: number | null;
  isMeasured: boolean;
  isStale: boolean;
  surveyMonth?: string | null;
  lastCalculatedOn?: string | null;
  rows: ENPSCoverageRowView[];
}

// eNPS Theme Briefing
export interface ENPSThemeBriefingView {
  summary?: string | null;
  generatedOn?: string | null;
  surveyMonth?: string | null;
  // Null from the read endpoint, which never checks the AI key; only a generate attempt answers it.
  aiAvailable?: boolean | null;
  noThemes?: boolean;
}

// eNPS Theme Extraction
export interface ENPSSuggestedThemeView {
  name: string;
  mentionCount: number;
}

export interface ENPSExtractionResultView {
  themesCreated: number;
  unclassifiedCount: number;
  suggestedNewThemes: ENPSSuggestedThemeView[];
  aiAvailable: boolean;
  insufficientFeedback: boolean;
  // CO-593 — the provider rejected the comments on content grounds. Distinct from `aiAvailable:
  // false`, which means nothing is configured. Here the service answered, and it answered no.
  contentRejected?: boolean;
  // CO-593 — what the numbers are actually based on (analysed), what the provider refused
  // (quarantined), and what was never sent because the run's time budget expired or it was
  // cancelled (notAttempted, says nothing about the provider). Absent on older responses.
  analysedCommentCount?: number;
  quarantinedCommentCount?: number;
  notAttemptedCommentCount?: number;
  // CO-869 — what a reader needs to tell a complete month from a short one. `written` is every
  // non-blank comment of the month before the cap; `overCap` is how many lay beyond the cap and
  // were never sent (they are in none of the three counts above); `contentRejected` is the
  // refused-on-content part of `quarantined`, the rest of which is ordinary faults. Absent when
  // the run never reached classification, and on responses from a server older than CO-869.
  writtenCommentCount?: number;
  overCapCommentCount?: number;
  contentRejectedCommentCount?: number;
}

// eNPS AI Suggestions
export interface ENPSFocusAreaSuggestionView {
  title: string;
  rationale?: string | null;
  reviewWindow?: string | null;
  themeCatalogId?: number | null;
}

export interface ENPSFocusAreaSuggestionsView {
  suggestions: ENPSFocusAreaSuggestionView[];
  aiAvailable: boolean;
  noThemes: boolean;
}

export interface ENPSActionSuggestionView {
  focusAreaId: number;
  focusAreaTitle: string;
  action: string;
  intent: 'resolve' | 'amplify';
}

export interface ENPSActionSuggestionsView {
  actions: ENPSActionSuggestionView[];
  aiAvailable: boolean;
  noFocusAreas: boolean;
}

// eNPS Employee Briefing
export interface ENPSEmployeeBriefingView {
  points: string[];
  surveyMonth?: string | null;
}

export interface ENPSEmployeeBriefingDraftView {
  points: string[];
  aiAvailable: boolean;
  noFocusAreas: boolean;
}

// Handoffs between the eNPS AI panel and the focus area editor; nothing here is persisted until HR acts.
export interface ENPSFocusAreaPrefill {
  title: string;
  rationale?: string | null;
  reviewWindow?: string | null;
  themeCatalogId?: number | null;
}

export interface ENPSRationaleAppend {
  focusAreaId: number;
  text: string;
}